import React from 'react';
import { Alert, Button, Box, Skeleton } from '@mui/material';
import {
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export interface TableStateViewProps {
  state:
    | 'loading'
    | 'empty'
    | 'no-results'
    | 'stale'
    | 'degraded'
    | 'error'
    | 'permission-limited'
    | 'offline';
  error?: Error | null;
  onRetry?: () => void;
  emptyMessage?: string;
  noResultsMessage?: string;
  staleMessage?: string;
  degradedMessage?: string;
  permissionMessage?: string;
  offlineMessage?: string;
  columnCount?: number;
}

export function TableStateView({
  state,
  error,
  onRetry,
  emptyMessage,
  noResultsMessage,
  staleMessage,
  degradedMessage,
  permissionMessage,
  offlineMessage,
  columnCount = 5,
}: TableStateViewProps) {
  const { t } = useTranslation();

  const renderState = () => {
    switch (state) {
      case 'loading':
        return (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2 }}
            role="status"
            aria-live="polite"
            aria-label={t('common:loading')}
          >
            <Box component="span" sx={{ position: 'absolute', left: '-10000px' }}>
              {t('common:loading')}
            </Box>
            {Array.from({ length: 5 }).map((_, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1 }}>
                {Array.from({ length: columnCount }).map((_, j) => (
                  <Box key={j} sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="80%" />
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        );

      case 'empty':
        return (
          <Alert severity="info" sx={{ mx: 1, my: 2 }}>
            {emptyMessage ?? t('common:states.empty.message')}
          </Alert>
        );

      case 'no-results':
        return (
          <Alert severity="info" sx={{ mx: 1, my: 2 }}>
            {noResultsMessage ?? t('common:states.noResults.message')}
          </Alert>
        );

      case 'stale':
        return (
          <Alert severity="warning" sx={{ mx: 1, my: 1 }}>
            <WarningIcon fontSize="small" sx={{ mr: 1 }} />
            {staleMessage ?? t('common:states.stale.message')}
          </Alert>
        );

      case 'degraded':
        return (
          <Alert severity="info" sx={{ mx: 1, my: 1 }}>
            <InfoIcon fontSize="small" sx={{ mr: 1 }} />
            {degradedMessage ?? t('common:states.degraded.message')}
          </Alert>
        );

      case 'error':
        return (
          <Alert severity="error" data-testid="table-error-state" sx={{ mx: 1, my: 2 }}>
            <ErrorIcon fontSize="small" sx={{ mr: 1 }} />
            {t('common:states.error.message')}
            {onRetry && (
              <Button
                size="small"
                onClick={onRetry}
                startIcon={<RefreshIcon fontSize="small" />}
                sx={{ ml: 2 }}
              >
                {t('common:retry')}
              </Button>
            )}
          </Alert>
        );

      case 'permission-limited':
        return (
          <Alert severity="info" sx={{ mx: 1, my: 2 }}>
            <InfoIcon fontSize="small" sx={{ mr: 1 }} />
            {permissionMessage ?? t('common:states.permissionLimited.message')}
          </Alert>
        );

      case 'offline':
        return (
          <Alert severity="warning" sx={{ mx: 1, my: 2 }}>
            <WarningIcon fontSize="small" sx={{ mr: 1 }} />
            {offlineMessage ?? t('common:states.offline.message')}
            {onRetry && (
              <Button
                size="small"
                onClick={onRetry}
                startIcon={<RefreshIcon fontSize="small" />}
                sx={{ ml: 2 }}
              >
                {t('common:retry')}
              </Button>
            )}
          </Alert>
        );

      default:
        return null;
    }
  };

  return <Box>{renderState()}</Box>;
}

export function SkeletonRow(columnCount: number) {
  return (
    <Box sx={{ display: 'flex', p: 1, borderBottom: 1, borderColor: 'divider' }}>
      {Array.from({ length: columnCount }).map((_, i) => (
        <Box key={i} sx={{ flex: 1, mr: 1 }}>
          <Skeleton variant="text" width="80%" />
        </Box>
      ))}
    </Box>
  );
}
